import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { WalletView } from '../app/components/WalletView';
import { RechargeStatusPage } from '../app/components/RechargeStatusPage';
import { ChargingSummaryPage } from '../app/components/ChargingSummaryPage';
import { useWalletStore } from '../store/walletStore';
import { useAuthStore } from '../store/authStore';
import { rechargeStatusAfterVerifyError } from '../services/walletService';
import { captureError } from '../lib/errorHandler';
import { addBreadcrumb, setPaymentContext } from '../lib/sentry';
import CashfreeUpi from '../plugins/cashfree-upi';

/**
 * Cashfree environment configuration from environment variables
 */
const CASHFREE_ENV = import.meta.env.VITE_CASHFREE_ENV;
if (!CASHFREE_ENV) {
  throw new Error('VITE_CASHFREE_ENV environment variable is not defined. Please set it in your .env file.');
}

/**
 * Load Cashfree SDK dynamically
 */
const loadCashfreeSdk = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if ((window as any).Cashfree) {
      const Cashfree = (window as any).Cashfree;
      resolve(Cashfree({ mode: CASHFREE_ENV }));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => {
      const Cashfree = (window as any).Cashfree;
      resolve(Cashfree({ mode: CASHFREE_ENV }));
    };
    script.onerror = () => reject(new Error('Failed to load Cashfree SDK'));
    document.head.appendChild(script);
  });
};

/**
 * WalletPage
 *
 * Wallet view with recharge status modal controlled by query parameters.
 *
 * Query Parameters:
 * - ?recharge=success&amount=500 → Shows successful recharge result
 * - ?recharge=failed&amount=500 → Shows failed recharge result
 * - ?order_id=xxx → Payment return from Cashfree (triggers verification)
 * - ?transaction=xyz → Shows transaction summary (when clicking wallet transaction)
 */
export function WalletPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Wallet store
  const { initiateRecharge, verifyPayment, fetchBalance } = useWalletStore();
  const user = useAuthStore((state) => state.user);
  const loadProfile = useAuthStore((state) => state.loadProfile);

  // Social sign-up can leave the account without a phone, and Cashfree rejects an
  // order with a null customer_phone. The backend now 400s before writing anything,
  // but that is a dead end for the user — send them to collect a number instead of
  // starting a payment that cannot succeed. Keyed on `phone`, not isPhoneVerified:
  // an admin-set number has the flag off and still works for Cashfree.
  //
  // The persisted user is not proof of a missing phone: /auth/login and
  // /auth/register omit `phone` from their response, so an email/password account
  // carries an undefined phone until a profile load fills it in. Confirm against the
  // server before sending anyone to collection.
  const ensurePhone = useCallback(async () => {
    if (user?.phone) return true;
    try {
      await loadProfile();
    } catch {
      // Transient failure — run the recharge and let the backend guard decide,
      // rather than dead-ending a user who does have a number.
      return true;
    }
    if (useAuthStore.getState().user?.phone) return true;
    navigate('/app/phone-collection', { state: { returnTo: '/app/wallet' } });
    return false;
  }, [user?.phone, loadProfile, navigate]);

  // Local state
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(0);
  const [newBalance, setNewBalance] = useState<number | undefined>(undefined);
  const [rechargeStatus, setRechargeStatus] = useState<'success' | 'failed' | 'pending'>('success');
  const [transactionId, setTransactionId] = useState<string | undefined>(undefined);
  const [walletTransactionId, setWalletTransactionId] = useState<string | undefined>(undefined);

  // Parse query parameters
  const rawRecharge = searchParams.get('recharge');
  const rechargeResult =
    rawRecharge === 'success' || rawRecharge === 'failed' || rawRecharge === 'pending'
      ? rawRecharge
      : null;
  const queryAmount = searchParams.get('amount');
  const queryTransactionId = searchParams.get('transaction');
  const orderId = searchParams.get('order_id');

  // Handle payment return URL (Cashfree redirects back with order_id)
  useEffect(() => {
    if (orderId) {
      verifyAndShowResult(orderId);
    }
  }, [orderId]);

  // Verify payment and show result
  const verifyAndShowResult = async (orderId: string) => {
    try {
      setIsProcessingPayment(true);
      setPaymentContext({ orderId });
      const result = await verifyPayment(orderId);

      setRechargeAmount(result.amount);
      setNewBalance(result.balance);
      setRechargeStatus(result.status === 'success' ? 'success' : 'failed');
      setTransactionId(orderId);
      setWalletTransactionId(result.walletTransactionId);
      setPaymentContext({ orderId, amount: result.amount, status: result.status });

      // Refresh wallet balance if successful
      if (result.status === 'success') {
        await fetchBalance();
      }

      // Update URL to show modal (remove order_id, add recharge status)
      setSearchParams({
        recharge: result.status === 'success' ? 'success' : 'failed',
        amount: result.amount.toString(),
      });
    } catch (error) {
      const status = rechargeStatusAfterVerifyError(error);
      if (status === 'failed') {
        captureError(error, 'WalletPage.verifyPayment', { orderId });
      } else {
        addBreadcrumb('wallet', 'Verify still pending after retries', { orderId }, 'warning');
      }
      setTransactionId(orderId);
      setRechargeStatus(status);
      setSearchParams({
        recharge: status,
        ...(rechargeAmount ? { amount: rechargeAmount.toString() } : {}),
      });
    } finally {
      setPaymentContext(null);
      setIsProcessingPayment(false);
    }
  };

  // Both native platforms now bridge to a Cashfree SDK: CashfreeUpiPlugin.java on
  // Android, CashfreeUpiPlugin.swift on iOS. iOS previously fell through to the web
  // path, where UPI is effectively unavailable — a upi:// link cannot leave the
  // WKWebView, so no installed UPI app is ever launched. The native SDK also avoids
  // depending on the returnUrl round-trip, which needs Universal Links that are not
  // live yet.
  const isNativeCheckout = Capacitor.isNativePlatform();

  // Native: use the Cashfree SDK with Drop Checkout (all payment methods)
  const handleNativeRecharge = useCallback(
    async (amount: number) => {
      if (!(await ensurePhone())) {
        return;
      }
      try {
        setRechargeAmount(amount);
        setIsProcessingPayment(true);

        const { orderId, paymentSessionId } = await initiateRecharge(amount);
        setPaymentContext({ orderId, amount });

        const cfEnv = CASHFREE_ENV === 'production' ? 'PRODUCTION' : 'SANDBOX';
        const result = await CashfreeUpi.startDropCheckout({
          paymentSessionId,
          orderId,
          environment: cfEnv,
        });

        console.log('[Recharge] Native checkout result:', result.status, result.orderId);

        if (result.status === 'SUCCESS') {
          await verifyAndShowResult(orderId);
        } else {
          captureError(
            new Error(result.errorMessage || 'Payment failed'),
            'WalletPage.nativeCheckout',
            { amount, orderId, errorCode: result.errorCode },
          );
          setRechargeStatus('failed');
          setSearchParams({ recharge: 'failed', amount: amount.toString() });
          setIsProcessingPayment(false);
        }
      } catch (error) {
        captureError(error, 'WalletPage.nativeRecharge', { amount });
        setRechargeStatus('failed');
        setSearchParams({ recharge: 'failed', amount: amount.toString() });
        setIsProcessingPayment(false);
      } finally {
        setPaymentContext(null);
      }
    },
    [initiateRecharge, setSearchParams, ensurePhone],
  );

  // Web: use Cashfree JS SDK with modal overlay
  const handleWebRecharge = useCallback(
    async (amount: number) => {
      if (!(await ensurePhone())) {
        return;
      }
      try {
        setRechargeAmount(amount);
        setIsProcessingPayment(true);

        // Cashfree needs an http(s) return URL and the backend validates it as
        // such. On iOS the WebView origin is capacitor://<hostname> (see the
        // iosScheme note in capacitor.config.ts), which fails that validation
        // with "returnUrl must be a valid HTTP or HTTPS URL" — a 400 before the
        // payment ever starts. The capacitor hostname is the real web domain in
        // both environments (poweresh.online / app.eveebuddy.com), so swapping
        // the scheme yields the correct https origin, which is also the domain
        // the Universal Link / App Link is registered against.
        const origin = window.location.origin;
        const returnUrlBase = origin.startsWith('capacitor://')
          ? origin.replace(/^capacitor:\/\//, 'https://')
          : /^https?:\/\/localhost$/.test(origin)
            ? 'https://poweresh.online'
            : origin;

        const { orderId, paymentSessionId } = await initiateRecharge(amount, returnUrlBase);

        const cashfree = await loadCashfreeSdk();

        const checkoutResult = await cashfree.checkout({
          paymentSessionId,
          returnUrl: `${returnUrlBase}/app/wallet?order_id=${orderId}`,
          redirectTarget: '_modal',
        });

        if (checkoutResult.error) {
          captureError(
            new Error(JSON.stringify(checkoutResult.error)),
            'WalletPage.cashfreeCheckout',
            { amount, orderId },
          );
          setRechargeStatus('failed');
          setSearchParams({ recharge: 'failed', amount: amount.toString() });
          setIsProcessingPayment(false);
          return;
        }

        if (checkoutResult.redirect) {
          console.log('[Recharge] Cashfree fell back to redirect mode');
          return;
        }

        if (checkoutResult.paymentDetails) {
          console.log('[Recharge] Payment completed in modal, verifying...');
          await verifyAndShowResult(orderId);
          return;
        }

        console.warn('[Recharge] Unexpected checkout result:', checkoutResult);
        setIsProcessingPayment(false);
      } catch (error) {
        captureError(error, 'WalletPage.initiateRecharge', { amount });
        setRechargeStatus('failed');
        setSearchParams({ recharge: 'failed', amount: amount.toString() });
        setIsProcessingPayment(false);
      }
    },
    [initiateRecharge, setSearchParams, ensurePhone],
  );

  const handleRechargeClick = isNativeCheckout ? handleNativeRecharge : handleWebRecharge;

  // Auto-trigger recharge when navigated with ?recharge=true&amount=X (from map badge)
  const hasTriggeredAutoRecharge = useRef(false);
  useEffect(() => {
    const rechargeParam = searchParams.get('recharge');
    const amountParam = searchParams.get('amount');
    if (
      rechargeParam === 'true' &&
      amountParam &&
      !hasTriggeredAutoRecharge.current &&
      !isProcessingPayment
    ) {
      hasTriggeredAutoRecharge.current = true;
      const amount = parseFloat(amountParam);
      if (amount > 0) {
        // Clear the trigger params before starting payment
        setSearchParams({}, { replace: true });
        handleRechargeClick(amount);
      }
    }
  }, [searchParams, handleRechargeClick, isProcessingPayment, setSearchParams]);

  // Handle transaction click (for viewing charging session details)
  const handleTransactionClick = useCallback(
    (sessionId: string) => {
      // When user clicks transaction in wallet history
      setSearchParams({ transaction: sessionId });
    },
    [setSearchParams]
  );

  // Close all modals
  const closeModals = useCallback(() => {
    if (rechargeResult === 'pending') {
      void fetchBalance();
    }
    setSearchParams({});
    setTransactionId(undefined);
    setWalletTransactionId(undefined);
    setNewBalance(undefined);
  }, [setSearchParams, rechargeResult, fetchBalance]);

  return (
    <>
      {/* Wallet View */}
      <WalletView
        onRechargeClick={handleRechargeClick}
        onTransactionClick={handleTransactionClick}
        isProcessingPayment={isProcessingPayment}
      />

      {/* Recharge Status Modal - only show when rechargeResult is in URL (after payment verification) */}
      {rechargeResult && (
        <RechargeStatusPage
          open={true}
          onOpenChange={(open) => !open && closeModals()}
          status={rechargeResult || rechargeStatus}
          amount={queryAmount ? parseFloat(queryAmount) : rechargeAmount}
          transactionId={transactionId}
          walletTransactionId={walletTransactionId}
          newBalance={newBalance}
        />
      )}

      {/* Transaction Summary Modal (for viewing past charging sessions from wallet) */}
      <ChargingSummaryPage open={!!queryTransactionId} onOpenChange={(open) => { if (!open) closeModals(); }} transactionId={queryTransactionId || undefined} />
    </>
  );
}
